import { Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import {
  EditPurchaseBodySchema,
  type MORTGAGE_TYPES,
  type Purchase,
  toBigNumber,
} from "shared";
import { AddressSelect } from "@/addresses/AddressSelect";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { FormGroup } from "@/components/form/FormGroup";
import { PriceInput, Transformers } from "@/components/form/PriceInput";
import { QueryKeys } from "@/LinkGen";

interface EditPurchaseAddressDialogProps {
  purchase: Purchase;
}

export const EditPurchaseAddressDialog = (
  props: EditPurchaseAddressDialogProps,
) => {
  const { purchase } = props;
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const [isTBD, setIsTBD] = useState(purchase.addressID === null);

  const { handleSubmit, formState, control, setValue, watch } = useForm({
    resolver: zodResolver(EditPurchaseBodySchema),
    reValidateMode: "onBlur",
    defaultValues: {
      mortgageType: purchase.mortgageType as (typeof MORTGAGE_TYPES)[number],
      price: purchase.price,
      downpayment: purchase.downpayment,
      earnest: purchase.earnest,
      sellerCredit: purchase.sellerCredit,
      fundingDate: purchase.fundingDate,
      annualInsurance: purchase.annualInsurance,
      annualHOA: purchase.annualHOA,
      annualTax:
        purchase.annualTax == null ? null : new BigNumber(purchase.annualTax),
      addressID: purchase.addressID,
      vaFirstTime: purchase.vaFirstTime,
      vaExempt: purchase.vaExempt,
      note: purchase.note,
    },
  });

  const priceRaw = watch("price") as string;
  const price = useMemo(() => toBigNumber(priceRaw), [priceRaw]);

  const taxModes = useMemo(
    () => ({
      annual: {
        dp: 2,
        label: "Yr",
        transformer: Transformers.Base(),
        tooltip: "Per year",
      },
      monthly: {
        dp: 2,
        label: "Mo",
        transformer: Transformers.Fraction(12),
        tooltip: "Per month",
      },
      percentage: {
        dp: 2,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price per year",
      },
    }),
    [price],
  );

  const onSubmit = handleSubmit(async (values) => {
    await Api.PurchaseEdit(purchase.ID, values);
    await queryClient.refetchQueries({
      queryKey: QueryKeys.Purchase(purchase.ID),
    });
    dialog.close();
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Edit address</Dialog.Header>
      <Dialog.Body>
        <FormGroup label="TBD address" htmlFor="tbdAddress">
          <input
            id="tbdAddress"
            type="checkbox"
            checked={isTBD}
            onChange={(e) => {
              const next = e.target.checked;
              setIsTBD(next);
              setValue("addressID", null);
              setValue("annualTax", null);
            }}
          />
        </FormGroup>
        {isTBD ? (
          <FormGroup
            label="Annual tax"
            htmlFor="annualTax"
            required
            error={formState.errors.annualTax?.message}
          >
            <PriceInput
              placeholder="Tax amount"
              id="annualTax"
              control={control}
              name="annualTax"
              modes={taxModes}
              defaultMode="percentage"
              defaultTyped={0.6}
            />
          </FormGroup>
        ) : (
          <FormGroup
            label="Address"
            htmlFor="address"
            required
            error={formState.errors.addressID?.message}
          >
            <AddressSelect
              placeholder="Search address..."
              id="address"
              control={control}
              name="addressID"
            />
          </FormGroup>
        )}
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
